export interface WebRTCConfig {
  iceServers: RTCIceServer[];
  iceCandidatePoolSize?: number;
}

export interface ICECandidateHandler {
  (candidate: RTCIceCandidate): void;
}

export interface ConnectionStateHandler {
  (state: RTCPeerConnectionState): void;
}

export enum CallState {
  IDLE = "idle",
  INITIALIZING = "initializing",
  CONNECTING = "connecting",
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",
  FAILED = "failed",
}

export class WebRTCManager {
  private peerConnection: RTCPeerConnection;
  private localStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;
  private iceCandidateHandler: ICECandidateHandler | null = null;
  private connectionStateHandler: ConnectionStateHandler | null = null;
  private currentState: CallState = CallState.IDLE;

  constructor(config: WebRTCConfig) {
    this.peerConnection = new RTCPeerConnection(config);
    this.setupPeerConnectionListeners();
  }

  private setupPeerConnectionListeners() {
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.iceCandidateHandler) {
        this.iceCandidateHandler(event.candidate);
      }
    };

    this.peerConnection.ontrack = (event) => {
      if (!this.remoteStream) {
        this.remoteStream = new MediaStream();
      }
      event.streams[0].getTracks().forEach((track) => {
        this.remoteStream?.addTrack(track);
      });
    };

    this.peerConnection.onconnectionstatechange = () => {
      const state = this.peerConnection.connectionState;
      console.log("WebRTC Connection state:", state);

      switch (state) {
        case "connecting":
          this.currentState = CallState.CONNECTING;
          break;
        case "connected":
          this.currentState = CallState.CONNECTED;
          break;
        case "disconnected":
          this.currentState = CallState.DISCONNECTED;
          break;
        case "failed":
          this.currentState = CallState.FAILED;
          break;
      }

      if (this.connectionStateHandler) {
        this.connectionStateHandler(state);
      }
    };

    this.peerConnection.oniceconnectionstatechange = () => {
      console.log(
        "ICE Connection state:",
        this.peerConnection.iceConnectionState
      );
    };
  }

  async initializeLocalStream(
    constraints: MediaStreamConstraints = { audio: true, video: false }
  ): Promise<MediaStream> {
    try {
      this.currentState = CallState.INITIALIZING;

      // Check for microphone permissions
      const permission = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      if (permission.state === "denied") {
        throw new Error("Microphone permission denied");
      }

      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
      this.localStream.getTracks().forEach((track) => {
        if (this.localStream) {
          this.peerConnection.addTrack(track, this.localStream);
        }
      });

      return this.localStream;
    } catch (error) {
      this.currentState = CallState.FAILED;
      console.error("Failed to get user media:", error);
      throw error;
    }
  }

  async createOffer(): Promise<RTCSessionDescriptionInit> {
    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    return offer;
  }

  async createAnswer(): Promise<RTCSessionDescriptionInit> {
    const answer = await this.peerConnection.createAnswer();
    await this.peerConnection.setLocalDescription(answer);
    return answer;
  }

  async setRemoteDescription(
    description: RTCSessionDescriptionInit
  ): Promise<void> {
    await this.peerConnection.setRemoteDescription(
      new RTCSessionDescription(description)
    );
  }

  async addIceCandidate(candidate: RTCIceCandidate): Promise<void> {
    try {
      await this.peerConnection.addIceCandidate(candidate);
    } catch (error) {
      console.error("Error adding ICE candidate:", error);
    }
  }

  setICECandidateHandler(handler: ICECandidateHandler): void {
    this.iceCandidateHandler = handler;
  }

  setConnectionStateHandler(handler: ConnectionStateHandler): void {
    this.connectionStateHandler = handler;
  }

  getPeerConnection(): RTCPeerConnection {
    return this.peerConnection;
  }

  getConnectionState(): RTCPeerConnectionState {
    return this.peerConnection.connectionState;
  }

  getCurrentState(): CallState {
    return this.currentState;
  }

  getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  getRemoteStream(): MediaStream | null {
    return this.remoteStream;
  }

  async getAudioDevices(): Promise<MediaDeviceInfo[]> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((device) => device.kind === "audioinput");
  }

  async getAudioOutputDevices(): Promise<MediaDeviceInfo[]> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((device) => device.kind === "audiooutput");
  }

  mute(): void {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((track) => {
        track.enabled = false;
      });
    }
  }

  unmute(): void {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((track) => {
        track.enabled = true;
      });
    }
  }

  isMuted(): boolean {
    if (!this.localStream) return false;
    const audioTracks = this.localStream.getAudioTracks();
    return audioTracks.length > 0 && !audioTracks[0].enabled;
  }

  close(): void {
    this.currentState = CallState.DISCONNECTED;
    this.localStream?.getTracks().forEach((track) => track.stop());
    this.peerConnection.close();
    this.localStream = null;
    this.remoteStream = null;
  }
}
